import { FC, Fragment, useCallback, useMemo, useState } from 'react';

import InputText, { InputScale, InputType } from '@hh.ru/bloko/build/blocks/inputText';

import Checkbox from 'bloko/blocks/checkbox';
import Tip from 'bloko/blocks/drop/Tip';
import HoverTip, { TipPlacement } from 'bloko/blocks/drop/Tip/HoverTip';
import HSpacing from 'bloko/blocks/hSpacing';
import Icon, { IconAppearance, IconColor, IconKind, IconLink, IconScale, IconView } from 'bloko/blocks/icon';
import Link from 'bloko/blocks/link';
import Text, { TextImportance } from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';
import FuzzySearch from 'bloko/common/fuzzySearch';

import useTrls from 'hooks/translation/useTrls';

import { FilterItem } from 'components/Dashboard/Filters/consts';

import { FilterName } from 'store/models/dashboard';

import styles from 'components/Dashboard/Filters/Filter/Filter.module.less';

interface FilterProps {
    filterName: FilterName;
    legend: string;
    filterList: FilterItem[];
    checkedValues: string[];
    onChange: (filterName: FilterName, value: string) => void;
}

const MAX_CHECKBOX_SHOWN_COUNT = 5;
const CHECKBOX_COUNT_TO_SHOW_SEARCH = 8;
const OVERLY_MASSIVE_FILTERS: FilterName[] = ['areas', 'professionalRoles', 'createdByUserIds'];

const TrlKeys = {
    expandButton: 'Dashboard.Filters.Filter.expandButton',
    searchPlaceholder: 'Dashboard.Filters.Filter.search.placeholder',
    searchEmptyWarning: 'Dashboard.Filters.Filter.search.emptyWarning',
};

const Filter: FC<FilterProps> = ({ filterName, legend, filterList, checkedValues, onChange }) => {
    const isCountGreaterThanMax = filterList.length > MAX_CHECKBOX_SHOWN_COUNT;
    const needHideExcessCheckboxes = OVERLY_MASSIVE_FILTERS.includes(filterName);
    const needShowSearch =
        OVERLY_MASSIVE_FILTERS.includes(filterName) && filterList.length >= CHECKBOX_COUNT_TO_SHOW_SEARCH;
    const [showExpandButton, setShowExpandButton] = useState(needHideExcessCheckboxes && isCountGreaterThanMax);
    const [searchValue, setSearchValue] = useState('');

    const trl = useTrls();

    const searchFilterItemsByName = useCallback(
        (item: FilterItem) => FuzzySearch.match(searchValue, item.name),
        [searchValue]
    );

    const filteredCheckboxes = useMemo(() => {
        const filteredFilterList = filterList.filter(searchFilterItemsByName);
        return showExpandButton && !searchValue
            ? filteredFilterList.slice(0, MAX_CHECKBOX_SHOWN_COUNT)
            : filteredFilterList;
    }, [filterList, searchFilterItemsByName, searchValue, showExpandButton]);

    const onSearchValueChange = (value: string) => {
        setSearchValue(value);
    };

    return (
        <div>
            <Text strong>{legend}</Text>
            <VSpacing base={3} />
            {needShowSearch && (
                <>
                    <InputText
                        onChange={onSearchValueChange}
                        placeholder={trl(TrlKeys.searchPlaceholder)}
                        prefix={
                            <Icon
                                appearance={IconAppearance.Outlined}
                                kind={IconKind.Default}
                                scale={IconScale.Small}
                                view={IconView.Lupa}
                            />
                        }
                        scale={InputScale.Small}
                        showClearButton
                        type={InputType.Search}
                        value={searchValue}
                        data-qa={`search-${filterName}-input`}
                    />
                    <VSpacing base={4} />
                </>
            )}
            {filteredCheckboxes.length > 0 ? (
                filteredCheckboxes.map(({ id, name, tip }) => (
                    <Fragment key={`${id}`}>
                        <div className={styles.checkbox}>
                            <Checkbox
                                data-qa={`filter-item_${filterName}_${id}`}
                                checked={checkedValues.includes(id)}
                                onChange={() => onChange(filterName, id)}
                            >
                                {name}
                            </Checkbox>
                            {tip && (
                                <>
                                    <HSpacing base={2} />
                                    <HoverTip
                                        placement={TipPlacement.Right}
                                        render={() => <p className={styles.tip}>{tip}</p>}
                                        host={document.body}
                                        DropElement={Tip}
                                    >
                                        <IconLink>
                                            <Icon
                                                initial={IconColor.Gray50}
                                                view={IconView.QuestionMark}
                                                scale={IconScale.Small}
                                            />
                                        </IconLink>
                                    </HoverTip>
                                </>
                            )}
                        </div>
                        <VSpacing base={3} />
                    </Fragment>
                ))
            ) : (
                <div className={styles.searchEmptyWarning}>
                    <Text importance={TextImportance.Secondary}>{trl(TrlKeys.searchEmptyWarning)}</Text>
                </div>
            )}
            {showExpandButton && !searchValue && (
                <Link
                    onClick={() => {
                        setShowExpandButton(false);
                    }}
                    data-qa={`dashboard-filters-${filterName}-expand-button`}
                >
                    {trl(TrlKeys.expandButton)}
                </Link>
            )}
            <VSpacing base={1} />
        </div>
    );
};

export default Filter;
